import { useEffect, useState } from "react";
import { DollarSign } from "lucide-react";
import {
  Labeled,
  NumberField,
  Toolbar,
  SaveButton,
  ArrayInput,
  BooleanInput,
  Datagrid,
  DateField,
  Edit,
  FormDataConsumer,
  FunctionField,
  List,
  NumberInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  required,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { SpeakerInput } from "../../components";
import { authClient } from "../../server";
import { getTemporalWorkflow } from "../../hooks/useTemporalWorkflow";

const buckets = [
  { id: "needs_review", name: "Needs review" },
  { id: "eligible", name: "Ready to count" },
  { id: "excluded", name: "Excluded" },
];

/** Preview is a review draft. Applying the saved preview is the only credit-changing action. */
function RunControls() {
  const { filterValues, setFilters } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [values, setValues] = useState({
    organizationId: "1701",
    sourceCampaignId: "43040",
    targetCampaignId: "",
    creditPeriodId: "",
    campaignCreditSource: "",
    cutoff: "",
  });
  const [workflowId, setWorkflowId] = useState(null);
  const [status, setStatus] = useState("");
  // Restore the complete source configuration so navigation cannot silently substitute the default organization.
  useEffect(() => {
    if (!filterValues.target_campaign_id || status === "RUNNING") return;
    let active = true;
    authClient
      .get(
        `/admin/campaign-recurring-carryovers?target_campaign_id=${Number(filterValues.target_campaign_id)}&_start=0&_end=1`,
      )
      .then((result) => {
        const row = result.json?.[0];
        if (!active || !row) return;
        const cutoff = new Date(row.cutoff_at);
        const localCutoff = new Date(
          cutoff.getTime() - cutoff.getTimezoneOffset() * 60000,
        )
          .toISOString()
          .slice(0, 19);
        setValues((previous) => ({
          ...previous,
          organizationId: String(row.organization_id),
          sourceCampaignId: String(row.source_campaign_id),
          targetCampaignId: String(row.target_campaign_id),
          creditPeriodId: String(row.credit_period_id),
          campaignCreditSource: row.campaign_credit_source,
          cutoff: localCutoff,
        }));
      })
      .catch((error) => {
        if (active) notify(error.message, { type: "error" });
      });
    return () => {
      active = false;
    };
  }, [filterValues.target_campaign_id, status, notify]);

  useEffect(() => {
    if (!workflowId || status !== "RUNNING") return;
    let active = true;
    const timer = setInterval(async () => {
      const workflow = await getTemporalWorkflow(workflowId);
      if (!active || !workflow) return;
      const state = workflow.status?.name;
      if (state && state !== "RUNNING") {
        setStatus(state);
        refresh();
        notify(
          state === "COMPLETED"
            ? "Carryover calculation completed. Refresh the list to review results."
            : `Carryover workflow ${state.toLowerCase()}. Check the execution in Temporal.`,
          { type: state === "COMPLETED" ? "success" : "error" },
        );
      }
    }, 5000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [workflowId, status, refresh, notify]);

  async function start(mode) {
    try {
      if (!values.cutoff) throw new Error("Supply a cutoff date and time.");
      const input = {
        organizationId: Number(values.organizationId),
        sourceCampaignId: Number(values.sourceCampaignId),
        targetCampaignId: Number(values.targetCampaignId),
        creditPeriodId: Number(values.creditPeriodId),
        campaignCreditSource: values.campaignCreditSource,
        cutoffAt: new Date(values.cutoff).toISOString(),
        mode,
      };
      if (
        ![
          input.organizationId,
          input.sourceCampaignId,
          input.targetCampaignId,
          input.creditPeriodId,
        ].every((n) => Number.isInteger(n) && n > 0) ||
        !input.campaignCreditSource.trim()
      ) {
        throw new Error(
          "Supply the campaigns, credit period and existing campaign credit source.",
        );
      }
      if (mode === "apply") {
        const result = await authClient.get(
          `/admin/campaign-recurring-carryovers?target_campaign_id=${input.targetCampaignId}&_start=0&_end=1`,
        );
        const row = result.json?.[0];
        if (!row) throw new Error("Refresh a preview before applying.");
        if (
          Number(row.organization_id) !== input.organizationId ||
          Number(row.source_campaign_id) !== input.sourceCampaignId ||
          Number(row.credit_period_id) !== input.creditPeriodId ||
          row.campaign_credit_source !== input.campaignCreditSource ||
          new Date(row.cutoff_at).getTime() !== Date.parse(input.cutoffAt)
        ) {
          throw new Error(
            "These settings differ from the saved preview. Refresh the preview before applying.",
          );
        }
        input.snapshotRunId = row.snapshot_run_id;
        if (
          !window.confirm(
            "Apply the reviewed preview? This replaces carryover credits and recalculates campaign qualification.",
          )
        )
          return;
      }
      const id = `campaign-carryover-${input.targetCampaignId}-${Date.now()}`;
      setStatus("RUNNING");
      await authClient.post("/temporal/workflows/CampaignRecurringCarryover", {
        taskQueue: "general",
        workflowId: id,
        args: [input],
      });
      setWorkflowId(id);
      setFilters(
        { ...filterValues, target_campaign_id: input.targetCampaignId },
        {},
      );
    } catch (error) {
      setStatus("");
      notify(error.message, { type: "error" });
    }
  }
  return (
    <Box component="details" sx={{ mb: 2, p: 2 }}>
      <summary>Calculate or apply campaign carryover</summary>
      <Alert severity="info" sx={{ my: 2 }}>
        Refresh a preview, review the exceptions, then apply that preview. Saved
        reviews alone do not change progress or credits.
      </Alert>
      <Stack direction="row" flexWrap="wrap" gap={2}>
        {[
          ["organizationId", "Charidy organization ID"],
          ["sourceCampaignId", "Original campaign ID"],
          ["targetCampaignId", "New campaign ID"],
          ["creditPeriodId", "New credit period ID"],
        ].map(([key, label]) => (
          <MuiTextField
            key={key}
            label={label}
            type="number"
            value={values[key]}
            onChange={(e) => setValues({ ...values, [key]: e.target.value })}
          />
        ))}
        <MuiTextField
          label="Existing campaign credit source"
          value={values.campaignCreditSource}
          helperText="Use the same source as the campaign's Charidy credit sync."
          onChange={(e) =>
            setValues({ ...values, campaignCreditSource: e.target.value })
          }
          sx={{ minWidth: 320 }}
        />
        <MuiTextField
          label="Cutoff (your local time)"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 1 }}
          value={values.cutoff}
          onChange={(e) => setValues({ ...values, cutoff: e.target.value })}
        />
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          disabled={status === "RUNNING"}
          onClick={() => start("preview")}
        >
          Refresh preview
        </Button>
        <Button
          variant="contained"
          disabled={status === "RUNNING"}
          onClick={() => start("apply")}
        >
          Apply reviewed preview
        </Button>
      </Stack>
      {status && (
        <Typography sx={{ mt: 1 }}>
          Calculation: {status.toLowerCase()}
          {workflowId && ` (${workflowId})`}
        </Typography>
      )}
    </Box>
  );
}

function ReviewFields() {
  const record = useRecordContext();
  return (
    <>
      <Alert
        severity={
          record?.automatic_status === "needs_review" ? "warning" : "info"
        }
        sx={{ mb: 2 }}
      >
        {record?.explanation}
      </Alert>
      {record?.application_issue && (
        <Alert severity="warning">{record.application_issue}</Alert>
      )}
      <Labeled label="Billing email">
        <TextField source="billing_email" />
      </Labeled>
      <Labeled label="Current subscription found">
        <TextField
          source="evidence.providerId"
          emptyText="Not yet identified"
        />
      </Labeled>
      <Labeled label="Annual USD value found">
        <NumberField
          source="evidence.annualUsd"
          emptyText="Needs verification"
        />
      </Labeled>
      <Typography sx={{ mb: 2 }}>
        Verify standing at: {new Date(record?.cutoff_at).toLocaleString()}
      </Typography>
      <SelectInput
        source="review_decision"
        label="Decision"
        validate={required()}
        fullWidth
        choices={[
          { id: "automatic", name: "Use automatic checks" },
          { id: "include", name: "Confirm eligible" },
          { id: "exclude", name: "Exclude" },
        ]}
      />
      <FormDataConsumer>
        {({ formData }) =>
          formData.review_decision === "include" && (
            <>
              <Alert severity="info">
                Confirm the facts at the displayed cutoff. Fixed installment
                pledges cannot count. Enter each speaker's share, rather than
                giving every speaker the full amount.
              </Alert>
              <SelectInput
                source="review.provider"
                label="Current provider"
                defaultValue={record?.evidence?.provider}
                choices={[
                  { id: "stripe", name: "Charidy / Stripe" },
                  { id: "double", name: "Double" },
                ]}
                validate={required()}
              />
              <TextInput
                source="review.providerId"
                defaultValue={record?.evidence?.providerId}
                label="Verified current subscription / Double plan ID"
                validate={required()}
                fullWidth
              />
              <SelectInput
                source="review.stripeAccount"
                defaultValue={record?.evidence?.stripeAccount}
                label="Stripe account (required for Stripe)"
                choices={[
                  { id: "us", name: "US" },
                  { id: "canada", name: "Canada" },
                ]}
              />
              <BooleanInput
                source="review.active"
                label="Verified active and continuing to charge at the cutoff"
              />
              <BooleanInput
                source="review.unending"
                label="Verified unending commitment — no fixed installment pledge"
              />
              <NumberInput
                source="review.annualUsd"
                defaultValue={record?.evidence?.annualUsd}
                label="Annual recurring value (USD)"
                min={360}
                validate={required()}
              />
              <ArrayInput
                source="review.allocations"
                defaultValue={record?.evidence?.allocations || []}
                label="Speaker allocation"
              >
                <SimpleFormIterator inline>
                  <SpeakerInput source="speakerId" />
                  <NumberInput
                    source="annualUsd"
                    label="Annual USD share"
                    min={0.01}
                    validate={required()}
                  />
                </SimpleFormIterator>
              </ArrayInput>
            </>
          )
        }
      </FormDataConsumer>
      <TextInput
        source="review_reason"
        label="What did you verify, and where?"
        multiline
        fullWidth
        validate={required()}
        helperText="Saved with your user ID and the cutoff. A new cutoff requires a fresh eligible confirmation."
      />
      <Box component="details" sx={{ mt: 2 }}>
        <summary>Original evidence and review history</summary>
        <Typography
          component="pre"
          sx={{
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            fontSize: 12,
          }}
        >
          {JSON.stringify(
            {
              originalDonationId: record?.source_donation_id,
              evidence: record?.evidence,
              reviews: record?.review_history,
            },
            null,
            2,
          )}
        </Typography>
      </Box>
    </>
  );
}

function EditView(props) {
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      transform={(data) => ({
        review_decision: data.review_decision,
        review: data.review,
        review_reason: data.review_reason,
        expected_snapshot_run_id: data.snapshot_run_id,
        expected_review_revision: (data.review_history || []).length,
      })}
    >
      <SimpleForm
        toolbar={
          <Toolbar>
            <SaveButton />
          </Toolbar>
        }
      >
        <ReviewFields />
      </SimpleForm>
    </Edit>
  );
}

function ListView(props) {
  return (
    <List
      {...props}
      empty={false}
      title="Campaign recurring carryover"
      filterDefaultValues={{ review_bucket: "needs_review" }}
      actions={false}
      exporter={false}
      perPage={25}
      sort={{ field: "id", order: "ASC" }}
      filters={[
        <NumberInput
          key="campaign"
          source="target_campaign_id"
          label="New campaign ID"
          alwaysOn
        />,
        <SelectInput
          key="bucket"
          source="review_bucket"
          label="Review status"
          choices={buckets}
          alwaysOn
        />,
        <TextInput
          key="email"
          source="billing_email"
          label="Billing email"
          alwaysOn
        />,
      ]}
    >
      <Box>
        <RunControls />
        <Datagrid rowClick="edit" bulkActionButtons={false}>
          <TextField source="billing_email" label="Billing email" />
          <TextField
            source="source_donation_id"
            label="Charidy source record"
          />
          <FunctionField
            label="Review status"
            render={(r) =>
              buckets.find((b) => b.id === r.review_bucket)?.name ||
              r.automatic_status
            }
          />
          <TextField source="explanation" label="What needs checking" />
          <DateField source="cutoff_at" label="Cutoff" showTime />
          <DateField source="applied_at" label="Last applied" showTime />
        </Datagrid>
      </Box>
    </List>
  );
}

export const CampaignRecurringCarryovers = {
  category: "misc",
  name: "campaign-recurring-carryovers",
  roles: ["administrator"],
  options: { label: "Campaign Carryover" },
  icon: DollarSign,
  list: ListView,
  edit: EditView,
};
